import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, MessageCircle, Mail, Phone, FileText, Reply, ChevronDown } from "lucide-react";
import { leadStatusPill, LeadStatus } from "@/data/leads";
import { useLeads } from "@/hooks/useLeads";
import { updateLeadStatus } from "@/services/leads";

const methodIcon: Record<string, any> = { WhatsApp: MessageCircle, Email: Mail, Phone: Phone, Form: FileText };
const filters: (LeadStatus | "All")[] = ["All", "New", "Contacted", "Follow Up", "Closed"];

const AdminLeads: React.FC = () => {
  const { leads, loading, error, refresh } = useLeads();
  const [filter, setFilter] = useState<LeadStatus | "All">("All");
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = useMemo(
    () => leads.filter((l) => (filter === "All" ? true : l.status === filter)),
    [filter, leads]
  );

  const setStatus = async (id: string, status: LeadStatus) => {
    try {
      await updateLeadStatus(id, status);
      await refresh();
      toast.success(`Enquiry marked as ${status.toLowerCase()}.`);
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : "Unable to update the enquiry.");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">Inbox</p>
        <h1 className="text-2xl font-extrabold text-slate-900">Leads & Inquiries</h1>
        <p className="mt-1 text-sm text-slate-500">Every inquiry from your website is captured here — even if email delivery fails.</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
              filter === f ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`leads-filter-${f.toLowerCase().replace(/ /g, "-")}`}
          >
            {f} ({f === "All" ? leads.length : leads.filter((l) => l.status === f).length})
          </button>
        ))}
      </div>

      <div className="card-soft divide-y divide-slate-100">
        {loading && <div className="py-12 text-center text-slate-400">Loading enquiries...</div>}
        {error && <div className="py-12 text-center text-red-600">{error}</div>}
        {!loading && !error && rows.map((l) => {
          const Icon = methodIcon[l.method];
          const open = openId === l.id;
          return (
            <div key={l.id} data-testid={`lead-${l.id}`}>
              <button
                onClick={() => setOpenId(open ? null : l.id)}
                className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-slate-50/60"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{l.initials}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-bold text-slate-900">{l.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${leadStatusPill(l.status)}`}>{l.status}</span>
                  </div>
                  <div className="truncate text-xs text-slate-500">{l.vehicle}</div>
                </div>
                <div className="hidden items-center gap-1.5 text-xs text-slate-500 md:flex"><Icon className="h-3.5 w-3.5" /> {l.method}</div>
                <div className="hidden text-xs text-slate-400 sm:block">{l.date}</div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="bg-slate-50/70 px-5 pb-5 pt-1">
                  <p className="rounded-xl bg-white p-4 text-sm text-slate-600 ring-1 ring-slate-100">{l.message}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                    <a href={`tel:${l.phone.replace(/ /g, "")}`} className="inline-flex items-center gap-1.5 font-semibold text-blue-600"><Phone className="h-4 w-4" /> {l.phone}</a>
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 font-semibold text-blue-600"><Mail className="h-4 w-4" /> {l.email}</a>
                    <select value={l.status} onChange={(event) => void setStatus(l.id, event.target.value as LeadStatus)} className="ml-auto rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600" aria-label={`Update status for ${l.name}`}>{filters.filter((value): value is LeadStatus => value !== "All").map((value) => <option key={value}>{value}</option>)}</select>
                    <a href={`mailto:${l.email}?subject=${encodeURIComponent(`Re: ${l.vehicle}`)}`} className="btn-blue py-2"><Reply className="h-4 w-4" /> Reply</a>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {!loading && !error && rows.length === 0 && <div className="py-12 text-center text-slate-400">No leads in this category.</div>}
      </div>
    </div>
  );
};

export default AdminLeads;
